/**
 * Audit log (Owner/Admin, audit:read) — one stream over auth_log (sign-ins) + audit_log (data access +
 * admin actions). SERVER-side keyset pagination ("Load more"), so the shared client-paged Table is used
 * in paginated={false} mode (server order preserved) with the cursor driving the next page. Filters:
 * event type, user email (exact, on submit), and a date range. No raw PII is shown — just the decoded
 * label, actor, target and IP the server returns.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { PermissionGate } from "@/components/portal/PermissionGate";
import {
  Button,
  DateRangePicker,
  EmptyState,
  ErrorState,
  Input,
  Select,
  SkeletonRows,
  StatusPill,
  Table,
  type Column,
  type DateRange,
  type StatusTone,
} from "@/components/ui";
import { useRbac } from "@/context/RbacContext";
import { formatInstant } from "@/lib/format";
import type { ActivityCategory, ActivityItem, ActivityKind } from "@/api/types";

const KINDS: ActivityKind[] = [
  "login_success",
  "login_failed",
  "logout",
  "data_access",
  "money_access",
  "admin_action",
  "sync_now",
];

/** Next calendar day (YYYY-MM-DD) — used as an EXCLUSIVE upper bound so the whole end day is included
 *  (server filters `at < date_to`), avoiding the 23:59:59.999 sub-millisecond gap. */
function nextDay(d: string): string {
  const dt = new Date(`${d}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}

const CAT_TONE: Record<ActivityCategory, StatusTone> = {
  login_success: "ok",
  login_failed: "danger",
  logout: "neutral",
  data_access: "info",
  money_access: "warn",
  admin_action: "info",
  sync_now: "info",
  other: "neutral",
};

export function AuditPage() {
  const { t } = useTranslation();
  return (
    <PermissionGate
      permission="audit:read"
      fallback={<ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />}
    >
      <AuditBody />
    </PermissionGate>
  );
}

function AuditBody() {
  const { t } = useTranslation();
  const { hasPermission } = useRbac();

  const [kind, setKind] = useState<"" | ActivityKind>("");
  const [range, setRange] = useState<DateRange>({ start: "", end: "" });
  const [actorInput, setActorInput] = useState("");
  const [actor, setActor] = useState("");

  const params = {
    kind: kind || null,
    actor: actor || null,
    // Send boundaries in the portal's fixed zone (+07:00 BKK) so a picked calendar day maps to that BKK
    // day, not a 7h-skewed UTC window. date_to = the day AFTER the picked end, at midnight → a clean
    // half-open window [from, to) against the server's `at < date_to` (whole end day included, no gap).
    date_from: range.start ? `${range.start}T00:00:00+07:00` : null,
    date_to: range.end ? `${nextDay(range.end)}T00:00:00+07:00` : null,
  };

  const q = useInfiniteQuery({
    queryKey: ["activity", params],
    queryFn: ({ pageParam, signal }) =>
      api.activity({ ...params, cursor: pageParam, limit: 50 }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: hasPermission("audit:read"),
  });

  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  function detailText(item: ActivityItem): string {
    if (item.category === "admin_action" && item.detail) {
      const d = item.detail as { target_email?: string; from?: string; to?: string };
      const who = d.target_email ?? "";
      if (d.from && d.to) {
        const change = t("activity.roleChange", { from: d.from, to: d.to });
        return who ? `${who} · ${change}` : change;
      }
      return who || "—";
    }
    // Prefer the human account name/number (resolved server-side from sub_accounts) over the opaque id.
    // Fall back to the raw id only for a churned/renumbered account no longer in the mirror.
    if (item.target_account_name || item.target_account_number) {
      const name = item.target_account_name;
      const number = item.target_account_number;
      if (name && number) return `${name} · #${number}`;
      return name ?? `#${number}`;
    }
    if (item.target_account_id) return t("activity.account", { id: item.target_account_id });
    return "—";
  }

  const columns: Column<ActivityItem>[] = [
    {
      key: "time",
      header: t("activity.colTime"),
      numeric: true,
      cell: (r) => formatInstant(r.at),
    },
    {
      key: "actor",
      header: t("activity.colActor"),
      cell: (r) => r.actor_email ?? "—",
    },
    {
      key: "event",
      header: t("activity.colEvent"),
      cell: (r) => (
        <StatusPill tone={CAT_TONE[r.category]} className="cursor-default">
          {r.label}
        </StatusPill>
      ),
    },
    {
      key: "target",
      header: t("activity.colTarget"),
      cell: (r) => <span className="text-(--text-muted)">{detailText(r)}</span>,
    },
    {
      key: "ip",
      header: t("activity.colIp"),
      numeric: true,
      cell: (r) => <span className="tabular-mono text-(--text-muted)">{r.client_ip ?? "—"}</span>,
    },
  ];

  const hasFilters = kind !== "" || actor !== "" || range.start !== "" || range.end !== "";

  return (
    <section aria-labelledby="audit-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1
          id="audit-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {t("activity.title")}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("activity.subtitle")}</p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("activity.filterKind")}
          </span>
          <Select
            value={kind}
            onChange={(v) => setKind(v as "" | ActivityKind)}
            options={[
              { value: "", label: t("activity.allKinds") },
              ...KINDS.map((k) => ({ value: k, label: t(`activity.kind.${k}`) })),
            ]}
            aria-label={t("activity.filterKind")}
          />
        </label>

        <form
          className="flex flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            setActor(actorInput.trim());
          }}
        >
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("activity.filterActor")}
          </span>
          <Input
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            onBlur={() => setActor(actorInput.trim())}
            placeholder={t("activity.actorPlaceholder")}
            type="email"
            className="w-64"
            aria-label={t("activity.filterActor")}
          />
        </form>

        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("activity.from")} — {t("activity.to")}
          </span>
          <DateRangePicker
            value={range}
            onChange={setRange}
            aria-label={`${t("activity.from")} — ${t("activity.to")}`}
          />
        </label>

        {hasFilters && (
          <Button
            variant="ghost"
            onClick={() => {
              setKind("");
              setActorInput("");
              setActor("");
              setRange({ start: "", end: "" });
            }}
          >
            {t("activity.clear")}
          </Button>
        )}
      </div>

      {q.isError ? (
        <ErrorState title={t("state.errorTitle")} body={t("state.errorBody")} />
      ) : q.isPending ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="flex flex-col gap-3">
          <Table
            columns={columns}
            rows={items}
            rowKey={(r) => `${r.source}-${r.id}`}
            caption={t("activity.title")}
            paginated={false}
            empty={<EmptyState title={t("activity.empty")} body={t("activity.emptyBody")} />}
          />
          {items.length > 0 && (
            <div className="flex justify-center">
              {q.hasNextPage ? (
                <Button
                  variant="secondary"
                  loading={q.isFetchingNextPage}
                  onClick={() => q.fetchNextPage()}
                >
                  {t("activity.loadMore")}
                </Button>
              ) : (
                <span className="text-(length:--text-2xs) text-(--text-subtle)">
                  {t("activity.end")}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
